import { round } from "lodash";
import get from "lodash/get";
import { useTranslation } from "react-i18next";
import { PARAMETER_OPTIONS } from "../../../../constants";
import MetconDisplay from "../../../../parts/metcon/MetconDisplay";
import MetconExercisesList from "../../../../parts/metcon/MetconExercisesList";
import { getExerciseName } from "../../../../parts/modals/learn-more/blockHelpers";
import {
  mapParameter,
  parameterName,
} from "../../../../parts/other-exercise/CustomParametersSets";
import PRBadge from "../../../../parts/PRBadge";
import StatusIcon from "../../../../parts/StatusIcon";
import SvgIcon from "../../../../parts/SvgIcon";
import TrendIndicator from "../../../../parts/TrendIndicator";
import ToolTip from "../../../../parts/tooltip-portal";
import { useRillContext } from "../../../../rill/RillReact/share/base";
import { formattedTarget, getVelocityValue } from "../../../../utils/actuals";
import { flattenSetsOutcomes } from "../../../../utils/calendar-trends";
import { getComplexSetReps } from "../../../../utils/complex";
import convertToRiR from "../../../../utils/converters/convert-intensity";
import { formatMetconResult } from "../../../../utils/metcon";
import { isPROutcome } from "../../../../utils/personal-records";
import ExerciseVideoLine from "../../../dashboard/program-builder/components/ExerciseVideoLine";

const VIDEO_ICON = "/img/new-icons/play-gradient.svg";
const COMMENT_ICON = "/img/new-icons/message-icon.svg";
const { FREEFORM } = PARAMETER_OPTIONS;

const WeekDayExercise = ({ exercise, exercises, prefix }) => {
  const { t } = useTranslation("athlete-portal");
  const { locals } = useRillContext();
  const {
    activeUser,
    withWeight,
    fromMetric,
    dayPersonalRecords,
    activeUserToRequest,
  } = locals;

  const isComplex = exercises?.length > 0;
  if (isComplex) {
    exercise = exercises[0];
  }
  const isMetcon = exercise.primary === "metcon";
  // formatMetconResult dereferences its argument, so guard the unlogged case.
  const metconResult =
    isMetcon && exercise.metconResult
      ? formatMetconResult(exercise.metconResult)
      : "";
  const isMetconCompleted = exercise.metconResult?.status === "completed";
  const hasCustomParameters = Array.isArray(exercise.parameters);
  // The wk-set-num column already shows the set index, so drop "Sets" from
  // the custom-parameter columns to avoid a redundant duplicate column.
  const customColumns = hasCustomParameters
    ? exercise.parameters.filter((param) => param !== PARAMETER_OPTIONS.SETS)
    : [];

  const units = activeUser.units || locals.units || "imperial";
  const showTrends = !!locals.query?.relTrends;
  const prevSets = locals.previousOutcomes?.[String(exercise._id)];
  const prevFlatOutcomes = flattenSetsOutcomes(prevSets);
  const precision = units === "metric" ? 2 : 1;
  const unitsLabel = units === "metric" ? "kg" : "lb";
  const intensityMeasure =
    get(activeUserToRequest, "intensityMeasure", "rpe") === "rpe"
      ? "RPE"
      : "RiR";
  const isRiR = intensityMeasure === "RiR";
  const isPrimary =
    exercise.type === "primary" || exercise.type === "secondary";
  const defaultRepMax =
    exercise.type === "secondary" && exercise.secondaryRepMaximum
      ? exercise.secondaryRepMaximum
      : get(exercise, `repMaximums.${exercise.primary}`, 0);
  const repMaxPR = dayPersonalRecords?.repMaxPR;

  const hasPR =
    repMaxPR &&
    exercise.sets.some((set) =>
      Object.values(set.outcomes || {}).some((o) =>
        isPROutcome(exercise, o, repMaxPR)
      )
    );

  const displayName = getExerciseName(exercise);

  const paramCellValue = (source, param) => {
    if (!source) return "";
    const key = mapParameter(param);
    if (param === PARAMETER_OPTIONS.LOAD) {
      return source.load
        ? withWeight(
            round(fromMetric(source.load, units, false), 1),
            units,
            false
          )
        : "";
    }
    if (param === PARAMETER_OPTIONS.RPEAMRAP) {
      return source.rpe != null && source.rpe !== ""
        ? convertToRiR(source.rpe, !isRiR)
        : "";
    }
    const value = source[key];
    return value === undefined || value === null || value === "" ? "" : value;
  };

  // Compact chip letter for a custom-parameter trend indicator. The three core
  // metrics reuse the standard R/E/L keys; anything else falls back to its
  // initial (tooltip label disambiguates collisions like Reps/Rest).
  const trendLetter = (param) => {
    if (param === PARAMETER_OPTIONS.REPS) return "R";
    if (param === PARAMETER_OPTIONS.RPEAMRAP) return "E";
    if (param === PARAMETER_OPTIONS.LOAD) return "L";
    return parameterName(param, isRiR).charAt(0).toUpperCase();
  };

  const hasVideo =
    !isMetcon &&
    exercise.sets.some((set) => Object.values(set.videos || {}).some(Boolean));

  // Build flat rows across all set-groups (skipped for metcon: its "sets" are
  // movements in the workout, not repeated rounds — see MetconExercisesList).
  // trendOrdinal stays continuous across groups (it indexes prevFlatOutcomes
  // for week-over-week trend matching); baseIdx is the displayed set number
  // and resets to 0 for every group so each target (e.g. a 2x5 followed by a
  // 1x3) numbers its own sets 1,2,… instead of continuing the count.
  let trendOrdinal = 0;
  const rows = isMetcon
    ? []
    : exercise.sets.flatMap((set, si) => {
        const setCount = Number(set.sets || set.rounds || 1);
        const baseIdx = 0;
        const complexReps = isComplex
          ? getComplexSetReps(exercises, si).replace(/[()]/g, "")
          : null;

        return Array.from({ length: setCount }, (_, i) => {
          const rowOrdinal = trendOrdinal++;
          const outcomeEntry = set.outcomes?.[i];
          const video = set.videos?.[i];
          const comment = set.comments?.[i];
          const commentCell = (
            <td className="wk-col-cmt">
              {comment && (
                <ToolTip body={comment}>
                  <SvgIcon
                    src={COMMENT_ICON}
                    useCurrentColor
                    classNames="wk-icon-active"
                  />
                </ToolTip>
              )}
            </td>
          );
          const { outcome, status } = outcomeEntry || {};
          const isPR = isPROutcome(exercise, outcomeEntry, repMaxPR);
          const rowClass = isPR
            ? "ok pr"
            : status === "completed"
              ? "ok"
              : status === "missed"
                ? "missed"
                : "pending";
          const statusIconEl = (
            <>
              <span className="wk-status-inline">
                {status === "completed" || status === "missed" ? (
                  <StatusIcon status={status} />
                ) : (
                  // Draft (tracked, unresolved) or no outcome at all for this
                  // round - either way, blank next to a resolved sibling row
                  // reads as broken, so both get the same dot.
                  <div className="wk-status-dot" />
                )}
              </span>
              {isPR && (
                <span className="wk-status-inline wk-status-pr-star">★</span>
              )}
            </>
          );

          if (hasCustomParameters) {
            const source = outcomeEntry && status ? outcome : set;
            // Trend cells for coach-defined parameters: compare each tracked
            // numeric param against the same set position from prior weeks.
            // Freeform/Rounds are structural/text and can't trend.
            const prevOutcome = prevFlatOutcomes[rowOrdinal] ?? null;
            const trendCells = customColumns
              .filter(
                (param) =>
                  param !== FREEFORM && param !== PARAMETER_OPTIONS.ROUNDS
              )
              .map((param) => {
                const key = mapParameter(param);
                if (param === PARAMETER_OPTIONS.LOAD) {
                  return {
                    param,
                    metric: "load",
                    unit: unitsLabel,
                    curr:
                      outcome?.load != null
                        ? round(fromMetric(outcome.load, units), precision)
                        : null,
                    prev:
                      prevOutcome?.load != null
                        ? round(fromMetric(prevOutcome.load, units), precision)
                        : null,
                  };
                }
                if (param === PARAMETER_OPTIONS.RPEAMRAP) {
                  const currRpe = outcome?.[key];
                  const prevRpeVal = prevOutcome?.[key];
                  return {
                    param,
                    metric: "rpe",
                    unit: "",
                    curr:
                      currRpe != null && currRpe !== "AMRAP"
                        ? Number(currRpe)
                        : null,
                    prev:
                      prevRpeVal != null && prevRpeVal !== "AMRAP"
                        ? Number(prevRpeVal)
                        : null,
                  };
                }
                const currVal = outcome?.[key];
                const prevVal = prevOutcome?.[key];
                return {
                  param,
                  metric: key,
                  unit: "",
                  curr:
                    currVal != null && currVal !== "" ? Number(currVal) : null,
                  prev:
                    prevVal != null && prevVal !== "" ? Number(prevVal) : null,
                };
              });
            const hasPrevTrend = trendCells.some((c) => c.prev != null);
            return (
              <tr key={`${si}-${i}`} className={rowClass}>
                <td className="wk-set-num">
                  {statusIconEl}
                  {baseIdx + i + 1}
                </td>
                {customColumns.map((param) => (
                  <td key={param} className={`wk-col-${mapParameter(param)}`}>
                    {paramCellValue(source, param)}
                  </td>
                ))}
                {showTrends && (
                  <td className="wk-col-trend">
                    {status === "completed" && hasPrevTrend ? (
                      <span className="trend-icons">
                        {trendCells.map(
                          ({ param, curr, prev, metric, unit }) => (
                            <TrendIndicator
                              key={param}
                              curr={curr}
                              prev={prev}
                              metric={metric}
                              letter={trendLetter(param)}
                              label={parameterName(param, isRiR)}
                              unit={unit}
                            />
                          )
                        )}
                      </span>
                    ) : (
                      <span className="trend-icons" />
                    )}
                  </td>
                )}
                <td className="wk-col-vid">
                  {video && (
                    <ExerciseVideoLine
                      vidItem={video}
                      exerciseToEdit={exercise._id}
                      indexIndividualSet={i}
                      setIndex={si}
                      src={VIDEO_ICON}
                      utcDate={exercise.utcDate}
                      primary={exercise.primary ?? exercise.exercise?.primary}
                      secondary={
                        exercise.secondary ?? exercise.exercise?.secondary
                      }
                      type={exercise.type ?? exercise.exercise?.type}
                      movement={
                        exercise.movement ?? exercise.exercise?.movement
                      }
                      set={set}
                      outcomeEntry={outcomeEntry}
                      setComment={comment}
                    />
                  )}
                </td>
                {commentCell}
              </tr>
            );
          }

          if (outcomeEntry && status) {
            const load = outcome?.load
              ? withWeight(
                  round(fromMetric(outcome.load, units, false), 1),
                  units,
                  false
                )
              : "";
            const pct =
              defaultRepMax && outcome?.load
                ? `${round((outcome.load / defaultRepMax) * 100, 1)}%`
                : "";
            const vel = outcome?.velocity
              ? `${getVelocityValue(outcome.velocity)}`
              : "";
            const e1rmVal = outcome?.e1rm
              ? withWeight(
                  round(fromMetric(outcome.e1rm, units, false), 1),
                  units,
                  false
                )
              : "";

            const prevOutcome = prevFlatOutcomes[rowOrdinal] ?? null;
            const prevReps = prevOutcome?.reps ?? null;
            const prevRpe =
              prevOutcome?.rpe != null && prevOutcome.rpe !== "AMRAP"
                ? Number(prevOutcome.rpe)
                : null;
            const prevLoad =
              prevOutcome?.load != null
                ? round(fromMetric(prevOutcome.load, units), precision)
                : null;
            const dispLoad =
              outcome?.load != null
                ? round(fromMetric(outcome.load, units), precision)
                : null;
            const dispRpe =
              outcome?.rpe != null && outcome.rpe !== "AMRAP"
                ? Number(outcome.rpe)
                : null;

            return (
              <tr key={`${si}-${i}`} className={rowClass}>
                <td className="wk-set-num">
                  {statusIconEl}
                  {baseIdx + i + 1}
                </td>
                <td>{complexReps || (outcome?.reps ?? "")}</td>
                <td className="wk-col-rpe">
                  {convertToRiR(outcome?.rpe, !isRiR) ?? ""}
                </td>
                <td>{load}</td>
                <td className="wk-col-1rm">{isPrimary ? pct : ""}</td>
                <td className="wk-col-e1rm">{e1rmVal}</td>
                <td className="wk-col-vel">{vel}</td>
                {showTrends && (
                  <td className="wk-col-trend">
                    {status === "completed" &&
                    (prevReps != null ||
                      prevRpe != null ||
                      prevLoad != null) ? (
                      <span className="trend-icons">
                        <TrendIndicator
                          curr={outcome?.reps ?? null}
                          prev={prevReps}
                          metric="reps"
                          letter="R"
                          label="Reps"
                        />
                        <TrendIndicator
                          curr={dispRpe}
                          prev={prevRpe}
                          metric="rpe"
                          letter="E"
                          label={intensityMeasure}
                        />
                        <TrendIndicator
                          curr={dispLoad}
                          prev={prevLoad}
                          metric="load"
                          letter="L"
                          label="Load"
                          unit={unitsLabel}
                        />
                      </span>
                    ) : (
                      <span className="trend-icons" />
                    )}
                  </td>
                )}
                <td className="wk-col-vid">
                  {video && (
                    <ExerciseVideoLine
                      vidItem={video}
                      exerciseToEdit={exercise._id}
                      indexIndividualSet={i}
                      setIndex={si}
                      src={VIDEO_ICON}
                      utcDate={exercise.utcDate}
                      primary={exercise.primary ?? exercise.exercise?.primary}
                      secondary={
                        exercise.secondary ?? exercise.exercise?.secondary
                      }
                      type={exercise.type ?? exercise.exercise?.type}
                      movement={
                        exercise.movement ?? exercise.exercise?.movement
                      }
                      set={set}
                      outcomeEntry={outcomeEntry}
                      setComment={comment}
                    />
                  )}
                </td>
                {commentCell}
              </tr>
            );
          }

          const plannedLoad = set.load
            ? withWeight(
                round(fromMetric(set.load, units, false), 1),
                units,
                false
              )
            : "";

          return (
            <tr key={`${si}-${i}`} className="pending">
              <td className="wk-set-num">
                {statusIconEl}
                {baseIdx + i + 1}
              </td>
              <td>{complexReps || (set.reps ?? "")}</td>
              <td className="wk-col-rpe">
                {convertToRiR(set.rpe, !isRiR) ?? ""}
              </td>
              <td>{plannedLoad}</td>
              <td className="wk-col-1rm">
                {isPrimary && set.percentRepMax
                  ? `${round(set.percentRepMax, 1)}%`
                  : ""}
              </td>
              <td className="wk-col-e1rm" />
              <td className="wk-col-vel" />
              {showTrends && <td className="wk-col-trend" />}
              <td className="wk-col-vid" />
              {commentCell}
            </tr>
          );
        });
      });

  return (
    <div className="wk-ex log-card__exercise">
      <div className="log-card__exercise-name flex items-center gap-1.5 min-w-0">
        {prefix && <span className="calendar-prefix">{prefix}</span>}
        <span className="min-w-0 flex-1 whitespace-normal wrap-break-word">
          {displayName}
        </span>
        {hasPR && <PRBadge />}
      </div>

      {!isMetcon &&
        exercise.sets.map((elem, idx) => (
          <div className="log-card__target" key={`${exercise._id}-${idx}`}>
            <span className="log-card__target-text">
              {formattedTarget(
                elem,
                isPrimary,
                elem.percentRepMax,
                units,
                elem.load
                  ? withWeight(
                      round(fromMetric(elem.load, units, false), 1),
                      units,
                      false
                    )
                  : null,
                intensityMeasure,
                isRiR,
                false,
                isComplex ? getComplexSetReps(exercises, idx) : undefined
              )}
            </span>
          </div>
        ))}

      {isMetcon ? (
        <div className="wk-metcon">
          <MetconDisplay exercise={exercise} compact />
          <MetconExercisesList
            exercise={exercise}
            setIndex={0}
            showCompletion
          />
          {metconResult && (
            <div className="wk-metcon-result">
              <span className="wk-metcon-result-label">
                {t("calendar.metconResult", "Result")}
              </span>
              <span
                className={`wk-metcon-result-value${
                  isMetconCompleted ? " completed" : ""
                }`}
              >
                {metconResult}
              </span>
            </div>
          )}
        </div>
      ) : (
        <table className="wk-set-table">
          <thead>
            <tr>
              <th className="wk-set-num">Sets</th>
              {hasCustomParameters ? (
                customColumns.map((param) => (
                  <th key={param} className={`wk-col-${mapParameter(param)}`}>
                    {param === FREEFORM ? "" : parameterName(param, isRiR)}
                  </th>
                ))
              ) : (
                <>
                  <th>Reps</th>
                  <th className="wk-col-rpe">{intensityMeasure}</th>
                  <th>Load</th>
                  <th className="wk-col-1rm">%1RM</th>
                  <th className="wk-col-e1rm">e1RM</th>
                  <th className="wk-col-vel">m/s</th>
                </>
              )}
              {showTrends && <th className="wk-col-trend">Trends</th>}
              <th className="wk-col-vid">{hasVideo ? "Video" : ""}</th>
              <th className="wk-col-cmt" />
            </tr>
          </thead>
          <tbody>{rows}</tbody>
        </table>
      )}

      {exercise.notes && (
        <div className="wk-ex-coach-note">
          <SvgIcon src={COMMENT_ICON} />
          <span>{exercise.notes}</span>
        </div>
      )}

      {exercise.athleteNotes && (
        <div className="wk-ex-note">{exercise.athleteNotes}</div>
      )}
    </div>
  );
};

export default WeekDayExercise;
